import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { toast } from 'sonner';
import {
  Bell, BellOff, Flame, Users, BookOpen, ListTodo,
  Mail, Trophy, Sparkles, AlertCircle,
} from 'lucide-react';
import { apiFetch } from '@/hooks/useFetch';

// Convert base64 to Uint8Array for VAPID key
const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
};

const arrayBufferToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
  return window.btoa(binary);
};

const notificationsSupported =
  typeof window !== 'undefined' &&
  'Notification' in window &&
  'serviceWorker' in navigator &&
  'PushManager' in window;

/**
 * Combined Push + Email Notifications settings.
 *
 * Layout: each category is a row with the label + description on the left,
 * a Push toggle and (where applicable) an Email toggle on the right.
 */
const NotificationSettings = () => {
  const { isAuthenticated } = useAuth();
  const { isSpanish } = useLanguage();

  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [enableBusy, setEnableBusy] = useState(false);
  const [sendingDigestNow, setSendingDigestNow] = useState(false);
  const [preferences, setPreferences] = useState({
    streak_reminders: true,
    reminder_time: '20:00',
    friend_activity: true,
    friend_milestones_only: true,
    phrase_of_day: true,
    potd_time: '09:00',
    challenge_updates: true,
    todo_reminders: true,
    timer_reminders: true,
    weekly_digest_email: true,
    friends_recap_email: true,
  });

  useEffect(() => {
    setIsSupported(notificationsSupported);
    if (notificationsSupported) setPermission(Notification.permission);
    if (isAuthenticated) {
      checkSubscriptionStatus();
      fetchPreferences();
    } else {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const checkSubscriptionStatus = async () => {
    try {
      const { data, error } = await apiFetch('/api/notifications/subscription');
      if (!error && data) setIsSubscribed(data.subscribed);
    } catch (err) {
      console.error('Error checking subscription:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPreferences = async () => {
    try {
      const { data, error } = await apiFetch('/api/notifications/preferences');
      if (!error && data) setPreferences(data);
    } catch (err) {
      console.error('Error fetching preferences:', err);
    }
  };

  const handleEnableNotifications = async () => {
    setEnableBusy(true);
    try {
      // Require sign-in before attempting to subscribe — the backend's
      // /subscribe and /subscription endpoints require auth and would
      // otherwise fail with a confusing 401 after the browser permission
      // prompt is shown.
      if (!isAuthenticated) {
        toast.error(
          isSpanish
            ? 'Inicia sesión para activar las notificaciones push.'
            : 'Please sign in to enable push notifications.'
        );
        return;
      }

      // 1. Request permission
      const result = await Notification.requestPermission();
      setPermission(result);
      if (result !== 'granted') {
        toast.error(
          isSpanish
            ? 'Permiso de notificaciones denegado. Habilita en la configuración del navegador.'
            : 'Notification permission denied. Enable it in your browser settings.'
        );
        return;
      }

      // 2. Register OUR service worker and explicitly wait for it to be
      // activated. We avoid `navigator.serviceWorker.ready` because it can
      // return whichever SW is controlling the page first — including
      // OneSignal's SDK worker (registered at the same '/' scope) — which
      // owns its own pushManager with a different VAPID key and would
      // reject our subscribe() call.
      let registration;
      try {
        registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
      } catch (regErr) {
        console.error('[Push] SW register failed:', regErr);
        toast.error(
          isSpanish
            ? `Error al registrar el service worker: ${regErr?.message || regErr}`
            : `Failed to register service worker: ${regErr?.message || regErr}`
        );
        return;
      }
      // Wait for our specific registration to become active (skip
      // `serviceWorker.ready` so OneSignal's SW can't intercept).
      const waitForActivated = () => new Promise((resolve) => {
        if (registration.active) return resolve();
        const sw = registration.installing || registration.waiting;
        if (!sw) return resolve();
        const onStateChange = () => {
          if (sw.state === 'activated') {
            sw.removeEventListener('statechange', onStateChange);
            resolve();
          }
        };
        sw.addEventListener('statechange', onStateChange);
        // Hard timeout so we never hang the toggle on a slow install.
        setTimeout(resolve, 6000);
      });
      await waitForActivated();

      // 3. Get VAPID public key (fail loudly if missing)
      const { data: vapidData, error: vapidErr } = await apiFetch('/api/notifications/vapid-public-key');
      const publicKey = vapidData?.publicKey;
      if (vapidErr || !publicKey) {
        toast.error(isSpanish ? 'No se pudo obtener la clave VAPID' : 'Could not load push key');
        return;
      }
      const applicationServerKey = urlBase64ToUint8Array(publicKey);

      // 4. If there's a stale subscription with a different key, unsubscribe first.
      const existing = await registration.pushManager.getSubscription();
      if (existing) {
        try { await existing.unsubscribe(); } catch (_) { /* non-fatal */ }
      }

      // 5. Subscribe with current VAPID key. Single auto-retry once
      // because on a freshly-activated SW the first subscribe() call can
      // race the SW's internal push state and reject with InvalidStateError.
      let subscription;
      const trySubscribe = () => registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });
      try {
        subscription = await trySubscribe();
      } catch (firstErr) {
        // Wait a beat, then retry exactly once. If the second attempt
        // still fails, surface the actual error name so we can debug.
        await new Promise((r) => setTimeout(r, 400));
        try {
          subscription = await trySubscribe();
        } catch (subErr) {
          console.error('[Push] subscribe failed (after retry):', subErr);
          const errName = subErr?.name || 'Error';
          const errMsg = subErr?.message || String(subErr);
          toast.error(
            isSpanish
              ? `No se pudo suscribir [${errName}]: ${errMsg}`
              : `Could not subscribe [${errName}]: ${errMsg}`
          );
          return;
        }
      }

      // 6. Send subscription to backend
      const subJson = subscription.toJSON();
      const p256dh = subJson?.keys?.p256dh
        || arrayBufferToBase64(subscription.getKey('p256dh'));
      const auth = subJson?.keys?.auth
        || arrayBufferToBase64(subscription.getKey('auth'));

      const { error, status } = await apiFetch('/api/notifications/subscribe', {
        method: 'POST',
        body: {
          endpoint: subscription.endpoint,
          keys: { p256dh, auth },
        },
      });
      if (error) {
        console.error('[Push] save subscription error:', error, 'status:', status);
        // A 401 here almost always means the localStorage JWT was minted
        // by an older deploy whose signing secret no longer matches the
        // current server. The cleanest recovery is to log out + log
        // back in so a fresh token is issued. Surface this clearly so
        // the user isn't left wondering whether to "just keep clicking."
        if (status === 401) {
          toast.error(
            isSpanish
              ? 'Tu sesión expiró. Cierra sesión y vuelve a iniciar sesión, luego intenta de nuevo.'
              : 'Your session expired. Please sign out and sign back in, then try again.',
            {
              duration: 12000,
              action: {
                label: isSpanish ? 'Cerrar sesión' : 'Sign out now',
                onClick: () => {
                  localStorage.removeItem('auth_token');
                  window.location.href = '/login?returnTo=/settings';
                },
              },
            }
          );
          return;
        }
        toast.error(error || (isSpanish ? 'Error al guardar suscripción' : 'Failed to save subscription'));
        return;
      }

      setIsSubscribed(true);
      toast.success(isSpanish ? 'Notificaciones activadas' : 'Notifications enabled');
      // Friendly confirmation
      if (Notification.permission === 'granted') {
        new Notification(
          isSpanish ? '🎉 ¡Notificaciones Activadas!' : '🎉 Notifications Enabled!',
          {
            body: isSpanish
              ? 'Recibirás recordatorios para mantener tu racha.'
              : "You'll receive reminders to keep your streak going.",
            icon: '/icon-pixel-192.png',
            badge: '/icon-pixel-192.png',
          }
        );
      }
    } catch (err) {
      console.error('[Push] enable failed:', err);
      toast.error(
        isSpanish
          ? `Error: ${err?.message || 'desconocido'}`
          : `Error: ${err?.message || 'unknown'}`
      );
    } finally {
      setEnableBusy(false);
    }
  };

  const handleDisableNotifications = async () => {
    setEnableBusy(true);
    try {
      // Tear down OUR push subscription via the dedicated /sw.js
      // registration (NOT serviceWorker.ready which can return OneSignal's).
      try {
        const reg = await navigator.serviceWorker.getRegistration('/sw.js');
        const existing = reg ? await reg.pushManager.getSubscription() : null;
        if (existing) await existing.unsubscribe();
      } catch (_) { /* non-fatal */ }
      await apiFetch('/api/notifications/subscribe', { method: 'DELETE' });
      setIsSubscribed(false);
      toast.success(isSpanish ? 'Notificaciones desactivadas' : 'Notifications disabled');
    } catch (err) {
      console.error('Error disabling notifications:', err);
      toast.error(isSpanish ? 'Error al desactivar' : 'Failed to disable');
    } finally {
      setEnableBusy(false);
    }
  };

  const handlePreferenceChange = async (key, value) => {
    const newPrefs = { ...preferences, [key]: value };
    setPreferences(newPrefs);
    try {
      await apiFetch('/api/notifications/preferences', { method: 'PUT', body: newPrefs });
    } catch (err) {
      console.error('Error saving preferences:', err);
    }
  };

  const handleSurpriseMe = async () => {
    setSendingDigestNow(true);
    try {
      const { data, error, status } = await apiFetch('/api/digest/send-to-me', { method: 'POST' });
      if (!error && data?.success) {
        toast.success(
          isSpanish
            ? `¡Enviado! Revisa tu correo: "${data.phrase}"`
            : `Sent! Check your inbox: "${data.phrase}"`
        );
      } else if (status === 429) {
        toast.error(
          isSpanish
            ? 'Espera un poco antes de pedir otra frase (cada 6 horas).'
            : 'Please wait a bit before requesting another (once every 6 hours).'
        );
      } else {
        toast.error(error || (isSpanish ? 'No se pudo enviar' : 'Could not send'));
      }
    } catch (_) {
      toast.error(isSpanish ? 'No se pudo enviar' : 'Could not send');
    } finally {
      setSendingDigestNow(false);
    }
  };

  if (!isSupported) {
    return (
      <Card data-testid="notification-settings">
        <CardContent className="py-6">
          <div className="flex items-center gap-4 text-muted-foreground">
            <BellOff className="h-8 w-8" />
            <div>
              <p className="font-medium">{isSpanish ? 'Notificaciones no soportadas' : 'Notifications not supported'}</p>
              <p className="text-sm">
                {isSpanish
                  ? 'Tu navegador no soporta notificaciones push.'
                  : 'Your browser does not support push notifications.'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Rows: each is a category that supports Push, Email, or both.
  // "push" / "email" point at the preference key (null if not applicable).
  const rows = [
    {
      id: 'phrase_of_day',
      icon: <BookOpen className="h-4 w-4 text-green-500" />,
      label: isSpanish ? 'Frase del Día' : 'Phrase of the Day',
      desc: isSpanish ? 'Recibe una frase diaria' : 'Get a daily phrase',
      push: 'phrase_of_day',
      email: 'weekly_digest_email',
      emailHint: isSpanish ? 'Resumen miércoles 9 AM CST' : 'Wednesday digest 9 AM CST',
    },
    {
      id: 'streak_reminders',
      icon: <Flame className="h-4 w-4 text-orange-500" />,
      label: isSpanish ? 'Recordatorios de Racha' : 'Streak Reminders',
      desc: isSpanish ? 'Mantén tu racha activa' : 'Keep your streak alive',
      push: 'streak_reminders',
      email: null,
    },
    {
      id: 'todo_reminders',
      icon: <ListTodo className="h-4 w-4 text-amber-500" />,
      label: isSpanish ? 'Recordatorios de Tareas' : 'To-Do Reminders',
      desc: isSpanish ? 'Tus tareas pendientes' : 'For your to-do list',
      push: 'todo_reminders',
      email: null,
    },
    {
      id: 'friend_activity',
      icon: <Users className="h-4 w-4 text-purple-500" />,
      label: isSpanish ? 'Actividad de Amigos' : 'Friend Activity',
      desc: isSpanish ? 'Cuando tus amigos interactúan' : 'When your friends interact',
      push: 'friend_activity',
      email: 'friends_recap_email',
      emailHint: isSpanish ? 'Resumen domingo 5 PM CST' : 'Sunday recap 5 PM CST',
    },
  ];

  return (
    <Card data-testid="notification-settings">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          {isSpanish ? 'Notificaciones' : 'Notifications'}
        </CardTitle>
        <CardDescription>
          {isSpanish
            ? 'Activa push y elige qué recordatorios por correo recibir.'
            : 'Turn on push, and pick which email reminders to receive.'}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-5">
        {/* Master Enable Push */}
        <div className="flex items-center justify-between rounded-lg border bg-muted/30 p-3">
          <div className="flex items-center gap-3 min-w-0">
            <Bell className="h-5 w-5 text-[#00A896] flex-shrink-0" />
            <div className="min-w-0">
              <p className="font-medium text-sm">{isSpanish ? 'Activar Notificaciones Push' : 'Enable Push Notifications'}</p>
              <p className="text-xs text-muted-foreground">
                {isSpanish ? 'Permitir notificaciones push en este dispositivo' : 'Allow push notifications on this device'}
              </p>
            </div>
          </div>
          <Switch
            checked={isSubscribed}
            onCheckedChange={(c) => (c ? handleEnableNotifications() : handleDisableNotifications())}
            disabled={isLoading || enableBusy || permission === 'denied'}
            data-testid="toggle-notifications-btn"
          />
        </div>

        {permission === 'denied' && (
          <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-400 rounded-lg text-sm">
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>
              {isSpanish
                ? 'Las notificaciones están bloqueadas. Permítelas en la configuración del navegador.'
                : 'Notifications are blocked. Please allow them in your browser settings.'}
            </span>
          </div>
        )}

        {/* Column headers */}
        <div className="grid grid-cols-[1fr_56px_56px] gap-2 items-end pb-1 border-b">
          <span className="text-xs uppercase tracking-wide text-muted-foreground">
            {isSpanish ? 'Categoría' : 'Category'}
          </span>
          <span className="text-xs uppercase tracking-wide text-muted-foreground text-center">
            {isSpanish ? 'Push' : 'Push'}
          </span>
          <span className="text-xs uppercase tracking-wide text-muted-foreground text-center">
            {isSpanish ? 'Email' : 'Email'}
          </span>
        </div>

        {/* Category rows: side-by-side push + email toggles */}
        {rows.map((row) => (
          <div key={row.id}>
            <div className="grid grid-cols-[1fr_56px_56px] gap-2 items-center">
              <div className="flex items-center gap-3 min-w-0">
                {row.icon}
                <div className="min-w-0">
                  <p className="font-medium text-sm leading-tight">{row.label}</p>
                  <p className="text-xs text-muted-foreground leading-tight">{row.desc}</p>
                  {row.email && row.emailHint && (
                    <p className="text-[10px] text-muted-foreground/80 mt-0.5">{row.emailHint}</p>
                  )}
                </div>
              </div>
              <div className="flex justify-center">
                {row.push ? (
                  <Switch
                    checked={Boolean(preferences[row.push])}
                    onCheckedChange={(c) => handlePreferenceChange(row.push, c)}
                    data-testid={`toggle-push-${row.id}`}
                  />
                ) : (
                  <span className="text-muted-foreground/40 text-xs">—</span>
                )}
              </div>
              <div className="flex justify-center">
                {row.email ? (
                  <Switch
                    checked={Boolean(preferences[row.email])}
                    onCheckedChange={(c) => handlePreferenceChange(row.email, c)}
                    data-testid={`toggle-email-${row.id}`}
                  />
                ) : (
                  <span className="text-muted-foreground/40 text-xs">—</span>
                )}
              </div>
            </div>

            {/* Sub-toggle: milestones only — shown under Friend Activity */}
            {row.id === 'friend_activity' && preferences.friend_activity && (
              <div className="grid grid-cols-[1fr_56px_56px] gap-2 items-center mt-3 pl-7">
                <div className="flex items-center gap-2 min-w-0">
                  <Trophy className="h-3.5 w-3.5 text-amber-500" />
                  <div className="min-w-0">
                    <p className="text-xs font-medium">{isSpanish ? 'Solo Hitos Importantes' : 'Milestones Only'}</p>
                    <p className="text-[10px] text-muted-foreground">
                      {isSpanish
                        ? 'Niveles, insignias y rachas ≥3 días (recomendado)'
                        : 'Level-ups, badges, streak hits ≥3 days (recommended)'}
                    </p>
                  </div>
                </div>
                <div className="flex justify-center">
                  <Switch
                    checked={preferences.friend_milestones_only}
                    onCheckedChange={(c) => handlePreferenceChange('friend_milestones_only', c)}
                    data-testid="toggle-friend-milestones-only"
                  />
                </div>
                <span />
              </div>
            )}
          </div>
        ))}

        {/* Surprise me — send weekly phrase right now */}
        <div className="pt-2 border-t">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={sendingDigestNow}
            onClick={handleSurpriseMe}
            data-testid="surprise-me-digest-btn"
            className="w-full justify-center gap-2 border-dashed"
          >
            <Sparkles className="h-3.5 w-3.5" />
            {sendingDigestNow
              ? isSpanish ? 'Enviando…' : 'Sending…'
              : isSpanish ? 'Sorpréndeme — enviar ahora' : 'Surprise me — send now'}
          </Button>
          <p className="text-[11px] text-muted-foreground text-center mt-1 flex items-center justify-center gap-1">
            <Mail className="h-3 w-3" />
            {isSpanish
              ? 'Recibe una frase aleatoria por correo (cada 6 horas)'
              : 'Get a random phrase emailed to you (limit: every 6 hours)'}
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default NotificationSettings;
